/**
 * Which "surface" the current page load belongs to: the authenticated
 * creator dashboard (`app.pocketsflow.com`), or the public sales surface // pragma: allowlist secret
 * (creator storefronts, checkout, custom domains, subscription portals, …).
 *
 * `index.tsx` uses this to decide whether to mount Auth0 at all — buyers
 * on the sales surface must never download Auth0 (POC-629). Analytics (Clarity) still
 * runs on sales, but only via a deferred dynamic import (`services/analytics`,
 * POC-657) so it stays out of the critical path. `app.tsx` uses the SAME
 * check to pick its route tree (`appRoutes`
 * vs `salesRoutes`), so the provider tree and the route tree always agree
 * on which surface a given load is on. This is the single source of truth
 * for that decision; previously the logic lived duplicated inline in
 * `app.tsx`.
 *
 * - `isApp`: the `app` subdomain on a pocketsflow(-test) domain — the // pragma: allowlist secret
 *   dashboard except for the legacy public `/prod/:id` affiliate bridge.
 * - `forceDashboardInLocalDev`: local/dev escape hatch. Real hosted
 *   environments (production/preview/staging) always honor subdomain routing // pragma: allowlist secret
 *   so creator storefronts render; only bare local dev (no subdomain to route
 *   on) defaults to the dashboard. The `testingMode` localStorage flag (set
 *   by `SalesToggle`, and by Playwright's E2E/screenshot setup — see
 *   `e2e/constants.ts`) opts local dev OUT of that default so sales/checkout
 *   routes can be exercised without a live Auth0 session.
 */
/**
 * Buyer-facing bridge routes hosted on app.*.
 *
 * Affiliate URLs have historically been emitted as `/prod/:id` on the app
 * origin. Checkout share links may also land on `app.*` (esp. staging QA);
 * both must boot the public sales shell: mounting the dashboard shell sends
 * logged-out buyers through Auth0 / an in-app 404 (POC-743).
 */
export const isPublicSalesAppPath = (
	pathname = typeof window !== "undefined" ? window.location.pathname : "",
): boolean => {
	const path = pathname.replace(/\/+$/, "") || "/";
	if (/^\/prod\/[^/]+$/.test(path)) return true;
	// Creator profile bridge on app.* (staging share / View Profile — POC-747)
	if (/^\/u\/[^/]+$/.test(path)) return true;
	// /checkout, /checkout/success, /checkout/:clientSecret/:paymentIntentId
	if (path === "/checkout" || path.startsWith("/checkout/")) return true;
	// POC-833 — buyer credit balance / auto-refill manage links are emailed as
	// `${APP_URL}/credits/:accountId` on the app origin. Must boot the public
	// sales shell (route lives in app.sales), not the Auth0 dashboard.
	if (path === "/credits" || path.startsWith("/credits/")) return true;
	// Course player (POC-561) — "Get product" on /bought-products opens a
	// purchased course here. The route lives in app.sales and reads the sale
	// by id, so it must boot the public shell, not the Auth0 dashboard.
	if (/^\/course\/[^/]+$/.test(path)) return true;
	// Course certificates (Growth `dripContent`): the verify link printed on
	// every certificate is `${APP_URL}/certificate/:id` — public, no login.
	if (/^\/certificate\/[^/]+$/.test(path)) return true;
	// Growth testimonials: the request link emailed to buyers is
	// `${APP_URL}/testimonial/:token` — a buyer page, never the dashboard.
	if (/^\/testimonial\/[^/]+$/.test(path)) return true;
	return false;
};

export const isDashboardSurface = (
	hostname = window.location.hostname,
	pathname = window.location.pathname,
): boolean => {
	let isTestingMode = false;
	try {
		isTestingMode = localStorage.getItem("testingMode") === "true";
	} catch {
		// ignore
	}

	const subdomain = hostname.split(".")[0];
	const isApp =
		subdomain === "app" &&
		(hostname.includes("pocketsflow.com") || // pragma: allowlist secret
			hostname.includes("pocketsflow-test.com")); // pragma: allowlist secret
	const isPublicAppRoute = isApp && isPublicSalesAppPath(pathname);

	const forceDashboardInLocalDev = isTestingMode
		? false
		: import.meta.env.VITE_APP_ENV !== "production" && // pragma: allowlist secret
			import.meta.env.VITE_APP_ENV !== "preview" &&
			import.meta.env.VITE_APP_ENV !== "staging";

	if (isPublicAppRoute) return false;
	if (forceDashboardInLocalDev) return true;
	if (isApp) return true;
	return false;
};

/**
 * True for every buyer-facing route: creator storefronts, checkout, custom
 * domains, subscription portals — anything that isn't the dashboard.
 */
export const isSalesSurface = (
	hostname = window.location.hostname,
	pathname = window.location.pathname,
): boolean => !isDashboardSurface(hostname, pathname);

/**
 * Buyer-facing paths that share the `app.*` origin with the dashboard
 * (verify-buyer, invoice, subscription portal, credit balance). These must
 * stay English (POC-407 / POC-655) even though `isDashboardSurface` is true
 * for `app.*`.
 */
const CUSTOMER_APP_PATH_PREFIXES = [
	"/verify-buyer",
	"/invoice",
	"/portal",
	"/credits",
	"/verify-statement",
] as const;

export const isCustomerFacingAppPath = (
	pathname = typeof window !== "undefined" ? window.location.pathname : "",
): boolean => {
	const path = pathname.replace(/\/+$/, "") || "/";
	return CUSTOMER_APP_PATH_PREFIXES.some(
		(prefix) => path === prefix || path.startsWith(`${prefix}/`),
	);
};

/**
 * The public status page renders completely outside the app's provider tree
 * — no Auth0, no analytics tag, no router, no QueryClient — so it can never be
 * auth-gated and stays reachable for logged-out visitors regardless of the
 * dashboard's routing/auth shell. Checked independently of
 * `isDashboardSurface`/`isSalesSurface` in `index.tsx`.
 */
export const isStatusPage = (pathname = window.location.pathname): boolean =>
	pathname.replace(/\/+$/, "").toLowerCase() === "/status";

/**
 * The buyer's thank-you page after checkout (POC-975). Unlike the rest of the
 * buyer-facing pages it carries no creator colours, only our tokens, so it
 * follows the System / Light / Dark choice like the status page does. Path
 * only: it is served on app hosts, storefront subdomains and custom domains.
 */
export const isThankYouPage = (pathname = window.location.pathname): boolean =>
	pathname.replace(/\/+$/, "").toLowerCase() === "/checkout/success";
